import React, { useState, useEffect } from 'react';
import { ArrowLeft, Info, Check, LayoutDashboard } from 'lucide-react'
import Bg from "./../Components/background/Bg"
import { useNavigate, useParams } from 'react-router-dom'
import api from '../api'
import { useAuth } from '../Context/AuthContext'
import ThemeChanger from '../Components/ThemeChanger';

export default function QuizSettingsPage() {
  const { auth } = useAuth()
  const [questionsPerTopic, setQuestionsPerTopic] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const navigate = useNavigate();
  const { topicId, subTopicId } = useParams();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [modules, setModules] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!auth.role || !auth.id) {
      navigate('/login')
    }
  }, [])

  useEffect(() => {
    if (!subTopicId || !topicId) return;
    api.get(`/user/get-all-subtopics?topicId=${topicId}`).then(res => {
      const response = res.data.data;
      if (!response || res.data.statusCode !== 200) {
        navigate('/dashboard');
        return
      }
      setSelectedTopic(response.filter(topic => topic._id === subTopicId)[0]);
    })
    api.get(`/user/get-modules?subTopicId=${subTopicId}`).then(res => {
      const response = res.data;
      if (!response || response.statusCode !== 200) {
        navigate('/dashboard');
        return
      }
      setLoading(true);
      setModules(response.data)
    })
  }, [subTopicId]);


  useEffect(() => {
    setTotalQuestions(modules.length * questionsPerTopic);
  }, [questionsPerTopic]);

  const handleQuestionsChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setQuestionsPerTopic(Math.min(Math.max(value, 0), 10)); // Limit between 0-10
  };

  const handleStartQuiz = () => {
    api.post("/user/create-roadmap", {
      topicId: topicId,
      subTopicId: subTopicId,
      questionsPerTopic: questionsPerTopic,
      totalQuestions: totalQuestions,
    }).then(res => {
      const response = res.data;
      if (response.statusCode === 200) {
        const roadMapId = response.id
        navigate(`/user-knowledge/${roadMapId}`);
      } else if (response.statusCode === 405) {
        navigate("/bmc")
      } else if (response.statusCode === 401 || response.statusCode === 403) {
        navigate("/dashboard")
      }

    })
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-base-200 relative p-3 sm:p-5 md:p-6 lg:p-8 flex justify-center items-start w-full">
      <Bg />
      <div className="w-full max-w-4xl relative z-10 ">

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">
              Quiz Settings
            </h1>
            <p className="text-sm sm:text-base text-base-content/70 mt-1">
              Configure your quiz for {selectedTopic?.name}
            </p>
          </div>
          <div className='flex justify-center items-center gap-4'>
            <ThemeChanger />
            <button
              onClick={handleBackToDashboard}
              className="btn btn-outline btn-info gap-2 btn-sm sm:btn-md"
            >
              <LayoutDashboard size={18} />
              <span className="xs:inline">Back to Dashboard</span>
            </button>
          </div>

        </div>


        <div className="bg-base-100 rounded-xl shadow-md p-4 sm:p-5 md:p-6">

          <div className="alert bg-[#FFC107]  text-[#212121] mb-5 font-[525]">
            <Info size={18} />
            <span>
              Your personalized roadmap will be generated based on
              your quiz performance.
            </span>
          </div>


          <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">
            Subtopics Included
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-5 sm:mb-6">
            {modules?.map((module, index) => (
              <div
                key={index}
                className="p-3 bg-base-200 rounded-lg flex items-center gap-2"
              >
                <Check
                  size={16}
                  className="text-primary flex-shrink-0"
                />
                <h3 className="text-sm sm:text-base font-medium">
                  {module}
                </h3>
              </div>
            ))}

            {!loading && Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="p-3 bg-base-200 rounded-lg flex items-center gap-2 animate-pulse"
              >
              </div>
            ))}
          </div>

          <div className="divider my-2 sm:my-4"></div>
          <h2 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4">
            Quiz Configuration
          </h2>
          <div className="alert alert-info mb-5 font-[525]">
            <Info size={18} />
            <span>
              More questions will result in a more accurate
              roadmap
            </span>
          </div>


          <div className="form-control w-full max-w-md mx-auto mb-5 sm:mb-6">
            <label className="label">
              <span className="label-text font-medium">
                Questions per subtopic
              </span>
              <span className="label-text-alt text-primary">
                (1–10)
              </span>
            </label>
            <input
              type="number"
              value={questionsPerTopic}
              onChange={handleQuestionsChange}
              className="input input-bordered w-full text-center"
              min="0"
              max="10"
            />
            <label className="label">
              <span className="label-text-alt">
                Recommended: 3–5 questions per subtopic
              </span>
            </label>
          </div>


          <div className="bg-base-200 p-3 sm:p-4 rounded-lg mb-5 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
              <div>
                <h3 className="text-sm sm:text-base font-medium">
                  Total Questions
                </h3>
                <p className="text-xs sm:text-sm text-base-content/70">
                  {modules?.length} subtopics ×{' '}
                  {questionsPerTopic} questions each
                </p>
              </div>
              <div className="text-xl sm:text-2xl font-bold text-primary">
                {totalQuestions}
              </div>
            </div>
          </div>


          <div className="alert bg-[#FFC107] mb-5 sm:mb-6 text-[#212121] font-[525]">
            <Info size={18} />
            <div>
              <span className="font-black">Important:</span> Your
              personalized roadmap will be generated based on how
              you perform in each subtopic. Ensure you answer the
              questions thoughtfully.
            </div>
          </div>


          <div className="flex justify-center">
            <button
              onClick={handleStartQuiz}
              disabled={totalQuestions === 0 || !loading}
              className="btn btn-primary btn-lg"
            >
              {totalQuestions === 0
                ? 'Select Number of Questions'
                : 'Start Quiz'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}